﻿import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavLink } from 'react-router-dom';
import AddBatchBenchSheet from './AddBatchBenchSheet';
import { ModalMessageType, BenchSheetTechnologyType } from '../models/enums';
import { LookupItem } from '../models/lookup-item';
import { BenchSheetCreation } from '../models/bench-sheet-creation';
import { CrudFunctions } from '../utilities/crud-functions';
import { BenchSheet } from '../models/benchsheet';
import { ModalMetadata } from '../models/modal-metadata';
import { ItemBasicInformation } from '../models/item-basic-information';
import GlobalDialog from './Modal/GlobalDialog';
import ItemBasicInformationDialog from './ItemBasicInformationDialog';
import { ToastProps } from '../models/toast-props';

import DataGrid, {
    Column,
    Paging,
    Toolbar,
    Selection,
    Item as ToolbarItem,
    Button as ColumnButton,
    LoadPanel
} from 'devextreme-react/data-grid';

import { Toast } from 'devextreme-react/toast';
import { Popup as DialogPopup } from 'devextreme-react/popup';
import { Button } from 'devextreme-react/button';

export interface Props {
    batchId: string;
    productId: string;
    isReadOnly: boolean;
}

export default function BatchBenchSheetData(props: Props) {
    const navigate = useNavigate();

    const [analyticalScopeTypes, setAnalyticalScopeTypes] =
        useState<LookupItem[]>();

    const [batchBenchSheets, setBatchBenchSheets] =
        useState<BenchSheet[]>();

    const [addBenchSheetDialogVisible, setAddBenchSheetDialogVisible] =
        useState<boolean>();

    const [toastProps, setToastProps] =
        useState<ToastProps>();

    const [itemBasicInformationDialogVisible, setItemBasicInformationDialogVisible] =
        useState<boolean>();

    const [itemBasicInformation, setItemBasicInformation] =
        useState<ItemBasicInformation>();

    const [globalDialogMetadata, setGlobalDialogMetadata] =
        useState<ModalMetadata>();

    const [globalDialogVisible, setGlobalDialogVisible] =
        useState<boolean>()

    useEffect(() => {
        getAllData();
    }, [props.batchId]);

    return (
        <div className="pw-margin-top-05">
            <div className="pw-comp-row">
                <div className="pw-form-unit">
                    <Button text="Create Bench Sheets" type="success"
                        hint="create all bench sheets"
                        height={'2.6em'} disabled={props.isReadOnly}
                        onClick={onCreateBenchSheetsClick} />
                </div>

                <div className="pw-form-unit">
                    <Button text="Add Bench Sheet" type="success"
                        hint="add new bench sheet"
                        height={'2.6em'}
                        onClick={onAddNewBenchSheetClick} />
                </div>
            </div>

            <div className="pw-form-unit pw-margin-top-05">
                <DataGrid
                    width="100%"
                    height="28rem"
                    dataSource={batchBenchSheets ?
                        batchBenchSheets : []}
                    onRowDblClick={onBatchBenchSheetsRowDoubleClick}
                    rowAlternationEnabled={true}
                    showBorders={true}>

                    <LoadPanel enabled={true} />
                    <Selection mode='single' />
                    <Paging enabled={false} />

                    <Column type="buttons" width="5rem">
                        <ColumnButton hint="information" icon="info"
                            onClick={onBenchSheetInformationButtonClicked} />
                        <ColumnButton render={gotoBenchSheetCellRender} />
                    </Column>

                    <Column dataField='state' width='3rem' cellRender={benchSheetStateCellRender} />
                    <Column dataField='benchSheetTechnologyName' caption="Technology" width='7rem' />
                    <Column dataField='createTimestamp' caption="Date"
                        dataType="date" format="MM/dd/yyyy" width='7rem' />
                    <Column dataField='methodDescription' width="20rem" />
                    <Column dataField='scopeTypesString' caption="Applicable Characterizations" width="20rem" />
                    <Column dataField='notes' />

                    <Toolbar>
                        <ToolbarItem location='before'>
                            <div className="pw-datagrid-toolbar-template">
                                <div className="pw-datagrid-toobar-title">Batch Bench Sheets</div>
                            </div>
                        </ToolbarItem>
                    </Toolbar>

                </DataGrid>
            </div>

            {addBenchSheetDialogVisible &&
                <DialogPopup
                    visible={addBenchSheetDialogVisible}
                    onHiding={hideAddBenchSheetDialog}
                    dragEnabled={false}
                    hideOnOutsideClick={false}
                    showTitle={true}
                    title="Add Bench Sheet"
                    width="50rem"
                    height="45rem">
                    <AddBatchBenchSheet batchId={props.batchId} productId={props.productId}
                        analyticalScopeTypes={analyticalScopeTypes ? analyticalScopeTypes : []}
                        onClose={onCloseAddBatchBenchSheet} />
                </DialogPopup>
            }

            {itemBasicInformationDialogVisible &&
                <DialogPopup visible={itemBasicInformationDialogVisible}
                    onHiding={hideItemBasicInformationDialog}
                    title="Item Basic Information"
                    width='25rem'
                    height='21rem'
                    dragEnabled={false}
                    resizeEnabled={false}>
                    <ItemBasicInformationDialog initInformation={itemBasicInformation}
                        onOkClick={hideItemBasicInformationDialog} />
                </DialogPopup>
            }

            {toastProps && toastProps.visible &&
                <Toast
                    visible={toastProps.visible}
                    message={toastProps.message}
                    type={toastProps.toastType}
                    onHiding={onToastHiding}
                    displayTime={600} />
            }

            {globalDialogVisible &&
                <GlobalDialog metadata={globalDialogMetadata} />
            }
        </div>
    );

    function onCloseAddBatchBenchSheet() {
        getBatchBenchSheets();
        hideAddBenchSheetDialog();
    }

    function onBatchBenchSheetsRowDoubleClick(e: any) {
        if (e.data && e.data.benchSheetTechnology && e.data.benchSheetId)
            navigate(`/benchsheet/${e.data.benchSheetTechnology}/${e.data.benchSheetId}`);
    }

    async function onCreateBenchSheetsClick() {
        await createBenchSheets()
            .then((data) => {
                let benchSheetCreations = data as BenchSheetCreation[];

                if (benchSheetCreations) {
                    let message = 'Bench Sheets created';

                    showToast(message, "success");

                    getBatchBenchSheets();
                }
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Creating Bench Sheets");
            });
    }

    function onAddNewBenchSheetClick() {
        showAddBenchSheetDialog();
    }

    async function getBatchBenchSheets() {
        await CrudFunctions.getData(`products/batchbenchsheets/${props.batchId}`)
            .then(data => {
                setBatchBenchSheets(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Bench Sheets");
            });
    }

    async function getAnalyticalScopeTypes() {
        await CrudFunctions.getData("common/analyticalscopetypes")
            .then(data => {
                setAnalyticalScopeTypes(data);
            })
            .catch((res) => {
                showMessage(res, ModalMessageType.Error, "Error Retrieving Analytical Scope Types");
            });
    }

    async function createBenchSheets() {
        return CrudFunctions.postData(`benchsheets/insertallbatchbenchsheets/${props.batchId}`, null);
    }

    async function getAllData() {
        await Promise.all([
            getBatchBenchSheets(),
            getAnalyticalScopeTypes()
        ]);
    }

    //show/hide

    function showAddBenchSheetDialog() {
        setAddBenchSheetDialogVisible(true);
    }

    function hideAddBenchSheetDialog() {
        setAddBenchSheetDialogVisible(false);
    }

    //render

    function benchSheetStateCellRender(e: any) {
        if (e.data) {
            if (e.data.benchSheetId)
                return <div><i className="dx-icon-runner"></i></div>;
            else
                return <div><i className="dx-icon-rename"></i></div>;
        }

        return <div><i className="dx-icon-help"></i></div>;
    }

    function gotoBenchSheetCellRender(e: any) {
        if (e.data) {
            let benchSheet = e.data as BenchSheet;

            if (benchSheet && benchSheet.benchSheetTechnology && benchSheet.benchSheetId) {
                let path = `/benchsheet/${benchSheet.benchSheetTechnology}/${benchSheet.benchSheetId}`;

                return <NavLink to={path} className="datagrid-icon-link"><span className="dx-icon-edit datagrid-icon column-button-margin"></span></NavLink>;
            }
            else
                return <div></div>;
        }

        return <div></div>;
    }

    function onBenchSheetInformationButtonClicked(e: any) {
        if (e.row && e.row.data) {
            const benchSheet = e.row.data as BenchSheet;

            if (benchSheet) {
                //setSelectedMethod(method);

                let itemInformation: ItemBasicInformation = {
                    rowPointer: benchSheet.benchSheetId,
                    createTimestamp: benchSheet.createTimestamp,
                    lastModifiedTimestamp: benchSheet.modifyTimestamp,
                    createUser: benchSheet.createUser,
                    modifyUser: benchSheet.modifyUser
                };

                setItemBasicInformation(itemInformation);
                setItemBasicInformationDialogVisible(true);
            }
        }
    }

    function hideItemBasicInformationDialog() {
        setItemBasicInformationDialogVisible(false);
    }

    function showToast(message: string, toastType: string) {
        let toastParams: ToastProps = {
            message: message,
            toastType: toastType,
            visible: true
        };

        setToastProps(toastParams);
    }

    function onToastHiding() {
        let toastParams: ToastProps = {
            message: undefined,
            toastType: undefined,
            visible: false
        };

        setToastProps(toastParams);
    }

    function showMessage(res: any, mode: ModalMessageType, title: string) {
        let mm: ModalMetadata = {
            error: res,
            mode: mode,
            title: title
        };

        setGlobalDialogMetadata(mm);
        setGlobalDialogVisible(true);
    }
}
